import React, { useEffect, useMemo, useState, useRef } from 'react';
import { toast } from 'sonner';
import { Copy, Eye, EyeOff, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';

/**
 * Modal de "Ver base completa" reutilizable.
 * - Sólo lectura. No edita datos.
 * - First column sticky.
 * - Search local + contador.
 * - Toggle de visibilidad por sección (no por columna individual para no abrumar).
 * - Copiar fila (TSV).
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - title: string (ej. "Base completa de Propietarios")
 *  - endpoint: string (path relativo a /api, ej. "/propietarios/preview-base")
 *  - queryParams: object (opcional) — filtros adicionales pasados como query
 *  - firstColumnIndex: number (default 0) — columna que queda fija a la izquierda
 *  - rowIdKey: 'propietario_id' | 'unidad_id' | string — clave para identificar la fila al copiar
 */
export const BaseCompletaDialog = ({
  open,
  onClose,
  title,
  endpoint,
  queryParams = {},
  firstColumnIndex = 0,
  rowIdKey = 'id',
  filterButtons = null,  // ReactNode opcional con botones de filtro extra
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ sections: [], rows: [], total: 0, filtered: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [hiddenSections, setHiddenSections] = useState({});
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const pickerRef = useRef(null);

  // Cerrar dropdown si se clickea afuera
  useEffect(() => {
    if (!showSectionPicker) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowSectionPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSectionPicker]);

  // Fetch
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(endpoint, { params: queryParams });
        if (!cancelled) {
          setData({
            sections: res.data.sections || [],
            rows: res.data.rows || [],
            total: res.data.total || 0,
            filtered: res.data.filtered ?? (res.data.rows || []).length,
          });
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Error al cargar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [open, endpoint, JSON.stringify(queryParams)]);

  // Build flat columns: [{label, sectionName, originalIdx}]
  const flatColumns = useMemo(() => {
    const out = [];
    let idx = 0;
    for (const s of data.sections) {
      for (const c of s.columns) {
        out.push({ label: c, sectionName: s.name, originalIdx: idx });
        idx += 1;
      }
    }
    return out;
  }, [data.sections]);

  const visibleColumns = useMemo(() => flatColumns.filter(c => !hiddenSections[c.sectionName]), [flatColumns, hiddenSections]);

  // Filter rows by search term (case insensitive, across all cells)
  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return data.rows;
    const q = searchTerm.trim().toLowerCase();
    return data.rows.filter(row =>
      row.values.some(v => String(v ?? '').toLowerCase().includes(q))
    );
  }, [data.rows, searchTerm]);

  const handleCopyRow = (row) => {
    // Build TSV: header line + value line
    const headers = visibleColumns.map(c => c.label);
    const values = visibleColumns.map(c => {
      const v = row.values[c.originalIdx];
      return v === null || v === undefined ? '' : String(v);
    });
    const tsv = headers.join('\t') + '\n' + values.join('\t');
    navigator.clipboard.writeText(tsv).then(
      () => toast.success('Fila copiada (TSV)'),
      () => toast.error('No se pudo copiar')
    );
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-900/70"
      data-testid="base-completa-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="m-4 flex-1 flex flex-col bg-white rounded-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            <span className="text-xs text-slate-500" data-testid="base-completa-counter">
              {loading ? 'cargando…' : `${filteredRows.length} / ${data.filtered} mostrados (total ${data.total})`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {filterButtons}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar en todas las columnas…"
                className="h-8 pl-8 w-64 text-sm"
                data-testid="base-completa-search"
              />
            </div>
            <div className="relative" ref={pickerRef}>
              <Button
                variant="outline" size="sm" className="h-8"
                onClick={() => setShowSectionPicker(s => !s)}
                data-testid="base-completa-columns-btn"
              >
                {Object.keys(hiddenSections).filter(k => hiddenSections[k]).length > 0
                  ? <EyeOff className="w-3.5 h-3.5 mr-1" />
                  : <Eye className="w-3.5 h-3.5 mr-1" />}
                Columnas
              </Button>
              {showSectionPicker && (
                <div className="absolute right-0 top-9 z-10 bg-white border border-slate-200 rounded-md shadow-lg p-2 min-w-[220px]">
                  <div className="text-xs text-slate-500 px-2 pb-1">Mostrar secciones</div>
                  {data.sections.map(s => (
                    <label key={s.name} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
                      <Checkbox
                        checked={!hiddenSections[s.name]}
                        onCheckedChange={(v) => setHiddenSections(prev => ({ ...prev, [s.name]: !v }))}
                        data-testid={`base-completa-section-toggle-${s.name}`}
                      />
                      <span className="text-sm">{s.name}</span>
                      <span className="text-xs text-slate-400 ml-auto">{s.columns.length}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-8" onClick={onClose} data-testid="base-completa-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        {loading && <div className="flex-1 flex items-center justify-center text-slate-500">Cargando…</div>}
        {error && <div className="flex-1 flex items-center justify-center text-rose-600">{error}</div>}
        {!loading && !error && (
          <div className="flex-1 overflow-auto" data-testid="base-completa-table-wrapper">
            <table className="text-xs border-collapse">
              <thead className="sticky top-0 z-20">
                {/* Sections row */}
                <tr className="bg-slate-700 text-white">
                  {data.sections.filter(s => !hiddenSections[s.name]).map((s, si) => (
                    <th
                      key={s.name}
                      colSpan={s.columns.length}
                      className={`px-2 py-1.5 text-center text-[10px] font-bold uppercase tracking-wider border border-slate-600 ${si === 0 ? 'sticky left-0 z-30 bg-slate-700' : ''}`}
                    >
                      {s.name}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-center text-[10px] font-bold uppercase tracking-wider border border-slate-600 bg-slate-700 sticky right-0">
                    
                  </th>
                </tr>
                {/* Column labels */}
                <tr className="bg-slate-100 sticky top-[28px]">
                  {visibleColumns.map((c, ci) => (
                    <th
                      key={`${c.sectionName}-${c.label}-${ci}`}
                      className={`px-2 py-1.5 text-left font-semibold text-slate-700 border border-slate-300 whitespace-nowrap ${ci === firstColumnIndex ? 'sticky left-0 z-20 bg-slate-100 min-w-[160px]' : ''}`}
                    >
                      {c.label}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-center font-semibold text-slate-700 border border-slate-300 bg-slate-100 sticky right-0">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, ri) => (
                  <tr
                    key={row[rowIdKey] || ri}
                    className="hover:bg-emerald-50/40"
                    data-testid={`base-completa-row-${ri}`}
                  >
                    {visibleColumns.map((c, ci) => {
                      let val = row.values[c.originalIdx];
                      const isNA = val === 'NO APLICA';
                      if (val === null || val === undefined) val = '';
                      if (typeof val === 'number') val = Number.isInteger(val) ? val : Math.round(val * 100) / 100;
                      const text = String(val);
                      return (
                        <td
                          key={ci}
                          className={`px-2 py-1 border border-slate-200 whitespace-nowrap ${ci === firstColumnIndex ? 'sticky left-0 z-10 bg-white font-medium' : ''} ${isNA ? 'text-slate-400 italic' : 'text-slate-700'}`}
                          title={text.length > 40 ? text : undefined}
                        >
                          <span className={text.length > 60 ? 'line-clamp-1 max-w-[280px] inline-block' : ''}>{text}</span>
                        </td>
                      );
                    })}
                    <td className="px-1 py-1 border border-slate-200 bg-white sticky right-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-slate-500 hover:text-slate-900"
                        onClick={() => handleCopyRow(row)}
                        data-testid={`base-completa-copy-${ri}`}
                        title="Copiar fila (TSV)"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="text-center py-8 text-slate-400">
                      Sin resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center justify-between">
          <span>Vista de sólo lectura · Para editar usá la pantalla principal.</span>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="base-completa-close-footer">
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
};
